/* eslint-disable react/no-danger */
import './styles.scss';
import React from 'react';
import PropTypes from 'prop-types';

import { Container } from '@edx/paragon';

import videoShape from '@prospectus/common/types/videoShape';
import SlantedLine from '@prospectus/common/ui/visual-elements/SlantedLine';

import { hideVideoForChina } from '@utils/videos';
import CloudflareImage from '@prospectus/common/ui/CloudflareImage';
import CourseBreadcrumbs from './components/CourseBreadcrumbs';
import CoursePartnerLogos from './components/CoursePartnerLogos';
import CourseVideo from './components/CourseVideo';
import CourseDescription from './components/CourseDescription';
import { RtlWrapper } from '../../../../utils/rtlLanguageHoc';

function CourseHeader({
  owners: partners,
  title,
  shortDescription: subtitle,
  video,
  originalImage,
  image,
  courseReview,
  mobile,
  h1Override,
  lastBreadcrumbTitle,
  prospectusPath,
}) {
  const courseTitle = lastBreadcrumbTitle ?? `${partners.map(obj => obj.key).join(', ')}: ${title}`;
  // small images for courses are 378px width, so image width:auto works
  // however for originalImage we need to set the width manually so the styling of the header won't break
  const imageParams = {
    width: '378', height: 'auto', quality: 85, format: 'webp',
  };
  const imgSrc = originalImage ? originalImage.src : image?.src;

  const videoOrImageJSX = () => {
    if (video && !hideVideoForChina()) {
      return (
        <CourseVideo
          title={title}
          video={video}
          image={image}
        />
      );
    }
    if (imgSrc) {
      return <CloudflareImage url={imgSrc} title={title} additionalParams={imageParams} className="header-image d-none d-sm-inline" />;
    }
    return null;
  };

  return (
    <div className="course-header">
      <Container size="lg" className="position-relative">
        <CourseBreadcrumbs
          courseName={courseTitle}
          prospectusPath={prospectusPath}
        />
        <CoursePartnerLogos
          partners={partners}
        />
        <SlantedLine className="header-line d-none d-md-block" fillClass="header-line-fill" size="small" />
        <RtlWrapper propsNames={['title', 'subtitle']}>
          <div className="row no-gutters">
            <CourseDescription
              title={h1Override || courseTitle}
              subtitle={subtitle}
              mobile={mobile}
              courseReview={courseReview}
            />
            <div className={`col-md-5 text-md-right ${mobile ? 'mt-12' : ''}`}>
              {videoOrImageJSX()}
            </div>
          </div>
        </RtlWrapper>
      </Container>
    </div>
  );
}

CourseHeader.propTypes = {
  owners: PropTypes.arrayOf(PropTypes.shape({
    uuid: PropTypes.string,
    slug: PropTypes.string,
    name: PropTypes.string,
    logoImageUrl: PropTypes.string,
    marketingUrl: PropTypes.string,
  })).isRequired,
  title: PropTypes.string.isRequired,
  shortDescription: PropTypes.string.isRequired,
  // TODO: define video shape https://2u-internal.atlassian.net/browse/SEO-704
  video: videoShape,
  image: PropTypes.shape({
    src: PropTypes.string.isRequired,
  }).isRequired,
  originalImage: PropTypes.shape({
    src: PropTypes.string.isRequired,
  }),
  mobile: PropTypes.bool,
  courseReview: PropTypes.shape({
    reviewsCount: PropTypes.number,
    avgCourseRating: PropTypes.number,
  }),
  h1Override: PropTypes.string,
  lastBreadcrumbTitle: PropTypes.string,
  prospectusPath: PropTypes.string.isRequired,
};

CourseHeader.defaultProps = {
  video: undefined,
  mobile: false,
  courseReview: null,
  h1Override: null,
  lastBreadcrumbTitle: null,
  originalImage: null,
};

export default CourseHeader;
